import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

function StocManager() {
  // Summary and list of groups (Layer 1)
  const [summary, setSummary] = useState({
    products: 0,
    items: 0,
    totalValue: 0,
    drafts: 0,
  });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create / edit / delete
  const [createOpen, setCreateOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  // Global search overlay (dialog for desktop)
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  const navigate = useNavigate();

  // API helpers (aligned with MobileFolderGroups)
  const runGlobalSearch = async (q) => {
    const query = (q || "").trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchError("");
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError("");
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/products-search",
        {
          params: { q: query, limit: 50 },
        }
      );
      setSearchResults(res.data?.results || []);
    } catch (err) {
      console.error("Eroare la /products-search:", err);
      setSearchError(err.response?.data?.error || "Eroare la căutare.");
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchLatestImageForGroup = async (groupId) => {
    try {
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups/${groupId}/latest-image`
      );
      return res.data.latestImage;
    } catch (err) {
      console.error(
        `Eroare la preluarea imaginii pentru grupul ${groupId}:`,
        err
      );
      return null;
    }
  };

  const fetchGroupTotals = async (groupId) => {
    try {
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups/${groupId}/summary`
      );
      return {
        totalProducts: res.data.products,
        totalValue: res.data.totalValue,
        itemsCount: res.data.items,
      };
    } catch (err) {
      console.error(`Eroare la summary pentru folder ${groupId}`, err);
      return { totalProducts: 0, totalValue: 0, itemsCount: 0 };
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups"
      );
      const updated = await Promise.all(
        res.data.map(async (group) => {
          const [latestImage, totals] = await Promise.all([
            fetchLatestImageForGroup(group.id),
            fetchGroupTotals(group.id),
          ]);
          return {
            ...group,
            image_url: latestImage,
            totalProducts: totals.totalProducts,
            totalValue: totals.totalValue,
            itemsCount: totals.itemsCount,
          };
        })
      );
      setGroups(updated);
    } catch (err) {
      console.error("Eroare la încărcarea grupurilor:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupSummary = async () => {
    try {
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folder-groups/summary"
      );
      setSummary({
        products: res.data.products,
        items: res.data.items,
        totalValue: res.data.totalValue,
        drafts: res.data.drafts || 0,
      });
    } catch (err) {
      console.error("Eroare la preluarea summary-ului:", err);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchGroupSummary();
  }, []);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folders",
        {
          name: newFolderName,
          parent_id: null,
        }
      );
      setCreateOpen(false);
      setNewFolderName("");
      await fetchGroups();
      setSnackbarMsg("Folderul a fost creat.");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Eroare creare folder:", err);
      setSnackbarMsg("Eroare la creare folder.");
      setSnackbarOpen(true);
    }
  };

  const updateFolderName = async (id) => {
    try {
      await axios.put(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${id}`,
        {
          name: editedName,
        }
      );
      setEditingId(null);
      await fetchGroups();
    } catch (err) {
      console.error("Eroare redenumire:", err);
    }
  };

  if (loading) {
    return (
      <Container sx={{ pt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ pt: 2, px: { xs: 1.5, md: 3 } }}>
      {/* Header with actions */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          Gestiune Stocuri
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setSearchOpen(true)}
          >
            Căutare globală
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Adaugă gestiune
          </Button>
        </Box>
      </Box>

      {/* Summary tiles (single row) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Bucăți
            </Typography>
            <Typography variant="h6">{summary.items || 0}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Produse
            </Typography>
            <Typography variant="h6">{summary.products}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Ciorne nefăcute
            </Typography>
            <Typography variant="h6">{summary.drafts || 0}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Valoare
            </Typography>
            <Typography variant="h6">
              {Number(summary.totalValue).toLocaleString()} RON
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Groups grid (Layer 1 only) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(520px, 1fr))",
          gap: 3,
        }}
      >
        {groups
          .filter((g) => Number(g.parent_id) === 0)
          .map((group) => (
            <Paper
              key={group.id}
              sx={{ p: 2, cursor: "pointer" }}
              onClick={() => {
                if (editingId !== group.id) navigate(`/layer2/${group.id}`);
              }}
            >
              <Box display="flex" alignItems="center" gap={2.5}>
                {/* Image preview */}
                {group.image_url ? (
                  <Box
                    component="img"
                    src={group.image_url}
                    alt={group.name}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1,
                      bgcolor: "#eee",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PhotoCameraIcon sx={{ color: "#999" }} />
                  </Box>
                )}
                <Box flex={1} minWidth={0}>
                  {editingId === group.id ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onBlur={() => updateFolderName(group.id)}
                      autoFocus
                    />
                  ) : (
                    <>
                      <Typography fontWeight={600} noWrap>
                        {group.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Produse: {group.totalProducts} • Bucăți:{" "}
                        {group.itemsCount} • Valoare:{" "}
                        {Number(group.totalValue).toLocaleString()} RON
                      </Typography>
                    </>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setSelectedGroup(group);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
      </Box>

      {/* Context menu for a group */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setEditingId(selectedGroup?.id);
            setEditedName(selectedGroup?.name || "");
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editare
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setConfirmDeleteOpen(true);
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Ștergere
        </MenuItem>
      </Menu>

      {/* Confirm delete */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirmare ștergere</DialogTitle>
        <DialogContent>
          <Typography>
            Ștergi gestiunea <strong>{selectedGroup?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Anulează</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              try {
                await axios.delete(
                  `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${selectedGroup.id}`
                );
                await fetchGroups();
                setSnackbarMsg("Folder șters.");
              } catch (err) {
                setSnackbarMsg("Eroare la ștergere.");
              } finally {
                setConfirmDeleteOpen(false);
                setSnackbarOpen(true);
              }
            }}
          >
            Șterge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create group dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
        <DialogTitle>Adaugă Gestiune Principală</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nume Folder"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleCreateFolder}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global search dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Căutare produse (global)
          <IconButton
            onClick={() => setSearchOpen(false)}
            sx={{ float: "right", mt: -0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder="Caută după titlu sau SKU"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runGlobalSearch(searchTerm);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <Button
                  onClick={() => runGlobalSearch(searchTerm)}
                  disabled={searchLoading || !searchTerm?.trim()}
                  startIcon={<SearchIcon />}
                >
                  Caută
                </Button>
              ),
            }}
          />
          <Box sx={{ mt: 2 }}>
            {searchLoading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography>Caut în toată baza…</Typography>
              </Box>
            )}
            {!searchLoading && searchError && (
              <Alert severity="error">{searchError}</Alert>
            )}
            {!searchLoading &&
              !searchError &&
              (searchResults?.length ?? 0) === 0 && (
                <Typography color="text.secondary">
                  {searchTerm?.trim()?.length < 2
                    ? "Introdu cel puțin 2 caractere, apoi caută."
                    : "Nu am găsit rezultate."}
                </Typography>
              )}
            {!searchLoading &&
              !searchError &&
              (searchResults?.length ?? 0) > 0 && (
                <Box>
                  {searchResults.map((p) => (
                    <Paper key={p.id} sx={{ p: 1.5, mb: 1 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        {p.image_url ? (
                          <Box
                            component="img"
                            src={p.image_url}
                            alt={p.product_name || ""}
                            sx={{
                              width: 56,
                              height: 56,
                              objectFit: "cover",
                              borderRadius: 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              bgcolor: "#e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1,
                            }}
                          >
                            <PhotoCameraIcon sx={{ color: "#9e9e9e" }} />
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography fontWeight={600} noWrap>
                            {p.product_name || "(fără titlu)"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            SKU: {p.sku || "-"} • Folder: {p.folder_name || "-"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            {Number(p.price || 0).toLocaleString()} RON
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setSearchOpen(false);
                              navigate(`/mobile-product/${p.id}`);
                            }}
                          >
                            Deschide
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default StocManager;
